import {
  GameObjects,
  Geom,
  Math as PhaserMath,
  Physics,
  Scene,
  type Input,
  type Types,
} from 'phaser';
import type { FieryRouteQuestionResult, FinishPlayAttemptInput } from '../../../../shared/results';
import {
  EventBus,
  ROUTE_GAME_FINISHED_EVENT,
  ROUTE_HUD_UPDATE_EVENT,
  SPECTACLE_ACTION_EVENT,
  SPECTACLE_ENTRANCE_EVENT,
  SPECTACLE_HIT_EVENT,
  type RouteGameResult,
  type RouteHudUpdate,
} from '../../../game/EventBus';
import { gateRoomAround, generateRouteMaze, type MazeCell, type RouteMaze } from '../domain/mazeGenerator';
import {
  nextContinuousDirection,
  ROUTE_DIRECTION_VECTORS,
  type RouteDirection,
} from '../domain/routeMovement';
import {
  FIERY_ROUTE_INVULNERABILITY_MS,
  enemyProfileForLevel,
  scoreAfterCorrect,
  scoreAfterWrongGate,
  FIERY_ROUTE_ANSWER_REVEAL_MS,
} from '../domain/routeRules';
import type { FieryRouteChoice, FieryRouteLevel } from '../domain/routeLevels';
import type { FieryRouteMode } from '../../../../shared/fieryRouteModes';
import { buildLearningReview } from '../../../game/domain/learningReview';
import { createLearningResultOverlay } from '../../../game/ui/LearningResultOverlay';
import { ROUTE_COLORS, ROUTE_GAME } from './Constants';
import { RouteAudio } from './RouteAudio';
import { createRouteGameState, type RouteGameState } from './RouteGameState';

type SceneInitData = { state?: RouteGameState };
type EnemyActor = {
  body: GameObjects.Arc;
  sprite: GameObjects.Image;
  drop: GameObjects.Arc;
  eyes: [GameObjects.Arc, GameObjects.Arc];
  spawn: MazeCell;
  nextRepathAt: number;
  targetCell?: MazeCell;
  direction: RouteDirection | null;
  portalCooldownUntil: number;
  cell: MazeCell;
  nextCell?: MazeCell;
};

type JoystickState = {
  pointerId: number | null;
  vector: PhaserMath.Vector2;
  base: GameObjects.Arc;
  knob: GameObjects.Arc;
};

export type FieryRouteSnapshot = {
  phase: RouteGameState['phase'];
  level: number;
  totalLevels: number;
  question: string;
  score: number;
  lives: number;
  wrongGates: number;
  caught: number;
  player: { x: number; y: number; invulnerable: boolean };
  enemies: Array<{ x: number; y: number }>;
  gates: Array<{ choice: string; correct: boolean; x: number; y: number }>;
  /** Oyuncu bir cevap alanında ama merkezde değilse o alanın seçenek indeksi (merkez işareti görünür). */
  gateHint: number | null;
  mode: FieryRouteMode;
  camera: { zoom: number; scrollX: number; scrollY: number };
};

export class AlevliRotaScene extends Scene {
  private static readonly KIVIL_TEXTURE = 'fiery-route-kivil';
  private static readonly CISE_TEXTURE = 'fiery-route-cise';
  private readonly levels: FieryRouteLevel[];
  private readonly gameId: string;
  private readonly restartEnabled: boolean;
  private readonly mediaTextureKeys = new Map<string, string>();
  private readonly audio = new RouteAudio();
  private state = createRouteGameState();
  private maze!: RouteMaze;
  private walls!: Physics.Arcade.StaticGroup;
  private player!: GameObjects.Arc;
  private playerSprite!: GameObjects.Image;
  private playerGlow!: GameObjects.Arc;
  private playerEyes!: [GameObjects.Arc, GameObjects.Arc];
  private playerFlameTip!: GameObjects.Triangle;
  private enemies: EnemyActor[] = [];
  private cursors?: Types.Input.Keyboard.CursorKeys;
  private wasd?: Record<'up' | 'down' | 'left' | 'right', Input.Keyboard.Key>;
  private joystick?: JoystickState;
  private overviewEndsAt = 0;
  /** Cevap alanı merkezindeki yarısaydam "buraya gir" işareti ve şu an gösterdiği geçit. */
  private gateHint?: GameObjects.Container;
  private gateHintIndex: number | null = null;
  private levelStartedAt = 0;
  private invulnerableUntil = 0;
  private portalCooldownUntil = 0;
  private resolving = false;
  private resultEmitted = false;
  private playerSpawnWorld = new PhaserMath.Vector2();
  private enemyProfile = enemyProfileForLevel(1);
  private playerDirection: RouteDirection | null = 'left';
  private requestedDirection: RouteDirection | null = 'left';
  private playerCell!: MazeCell;
  private playerNextCell?: MazeCell;
  private wrongChoiceIndices: number[] = [];

  private readonly mode: FieryRouteMode;
  /** Normal modda yanlış kapının ✕ işareti ve doğru kapının yarısaydam vurgusu. */
  private revealObjects: GameObjects.GameObject[] = [];

  constructor(levels: FieryRouteLevel[], gameId = 'fiery-route-game', restartEnabled = true, mode: FieryRouteMode = 'challenge') {
    super('AlevliRota');
    this.levels = levels;
    this.gameId = gameId;
    this.restartEnabled = restartEnabled;
    this.mode = mode;
  }

  init(data: SceneInitData) {
    this.state = data.state ?? createRouteGameState();
  }

  preload() {
    if (!this.textures.exists(AlevliRotaScene.KIVIL_TEXTURE)) {
      this.load.image(AlevliRotaScene.KIVIL_TEXTURE, '/odevlendir/assets/fiery-route/kivil.png');
    }
    if (!this.textures.exists(AlevliRotaScene.CISE_TEXTURE)) {
      this.load.image(AlevliRotaScene.CISE_TEXTURE, '/odevlendir/assets/fiery-route/cise.png');
    }
    const urls = new Set<string>();
    this.levels.forEach((level) => {
      if (level.promptMediaUrl) urls.add(level.promptMediaUrl);
      level.choices.forEach((choice) => { if (choice.mediaUrl) urls.add(choice.mediaUrl); });
    });
    [...urls].forEach((url, index) => {
      const textureKey = `${this.gameId}-route-media-${index}`;
      this.mediaTextureKeys.set(url, textureKey);
      if (!this.textures.exists(textureKey)) this.load.image(textureKey, url);
    });
  }

  create() {
    this.resolving = false;
    this.revealObjects = [];
    this.gateHintIndex = null;
    this.resultEmitted = false;
    this.enemies = [];
    this.playerDirection = 'left';
    this.requestedDirection = 'left';
    this.wrongChoiceIndices = [];
    this.state.phase = 'overview';
    const level = this.activeLevel;
    this.enemyProfile = enemyProfileForLevel(level.id);
    this.maze = generateRouteMaze(level.seed, level.choices.length);
    this.playerCell = { ...this.maze.playerSpawn };
    this.playerNextCell = undefined;
    this.createGardenWorld();
    this.createWalls();
    this.createGates();
    this.createGateHint();
    this.createPortals();
    this.createPlayer();
    this.createEnemies();
    this.createControls();
    this.configureCamera();
    this.installDebugHooks();
    this.refreshHud();
    EventBus.emit(SPECTACLE_ENTRANCE_EVENT, { template: 'fiery-route', level: level.id });
    EventBus.emit('current-scene-ready', this);
    this.events.once('shutdown', () => this.cleanupInput());
  }

  update(time: number, delta: number) {
    this.simulate(Math.min(delta, ROUTE_GAME.MAX_DELTA_MS), time);
  }

  private get activeLevel(): FieryRouteLevel {
    return this.levels[this.state.levelIndex];
  }

  private get worldWidth(): number {
    return this.maze.width * ROUTE_GAME.TILE_SIZE;
  }

  private get worldHeight(): number {
    return this.maze.height * ROUTE_GAME.TILE_SIZE;
  }

  private worldPoint(cell: MazeCell): PhaserMath.Vector2 {
    return new PhaserMath.Vector2(
      (cell.x + 0.5) * ROUTE_GAME.TILE_SIZE,
      (cell.y + 0.5) * ROUTE_GAME.TILE_SIZE,
    );
  }

  private createGardenWorld() {
    this.physics.world.setBounds(0, 0, this.worldWidth, this.worldHeight);
    this.cameras.main.setBounds(0, 0, this.worldWidth, this.worldHeight);
    this.add.rectangle(this.worldWidth / 2, this.worldHeight / 2, this.worldWidth, this.worldHeight, ROUTE_COLORS.garden);
    const garden = this.add.graphics();
    const random = new PhaserMath.RandomDataGenerator([this.activeLevel.seed]);
    for (let index = 0; index < 72; index += 1) {
      const x = random.between(12, this.worldWidth - 12);
      const y = random.between(12, this.worldHeight - 12);
      const color = index % 3 === 0 ? ROUTE_COLORS.gardenLight : index % 3 === 1 ? 0x6cac66 : 0xf2cc6b;
      garden.fillStyle(color, 0.12 + random.frac() * 0.16);
      garden.fillCircle(x, y, random.between(5, 14));
    }
  }

  private createWalls() {
    this.walls = this.physics.add.staticGroup();
    const size = ROUTE_GAME.TILE_SIZE;
    const pathBorders = this.add.graphics().setDepth(2);
    const gardenDetails = this.add.graphics().setDepth(1);
    const gateRoomKeys = new Set(this.maze.gateRoomCells.map((cell) => `${cell.x},${cell.y}`));
    for (let y = 0; y < this.maze.height; y += 1) {
      for (let x = 0; x < this.maze.width; x += 1) {
        if (this.maze.tiles[y][x] === 0) {
          const isGateRoom = gateRoomKeys.has(`${x},${y}`);
          this.add.rectangle(
            (x + 0.5) * size,
            (y + 0.5) * size,
            size,
            size,
            isGateRoom ? ROUTE_COLORS.gardenLight : ROUTE_COLORS.path,
          ).setDepth(1);
          pathBorders.lineStyle(3, ROUTE_COLORS.pathEdge, 0.96);
          const centerX = (x + 0.5) * size;
          const centerY = (y + 0.5) * size;
          if (this.maze.tiles[y - 1]?.[x] !== 0) pathBorders.lineBetween(centerX - size / 2, centerY - size / 2, centerX + size / 2, centerY - size / 2);
          if (this.maze.tiles[y + 1]?.[x] !== 0) pathBorders.lineBetween(centerX - size / 2, centerY + size / 2, centerX + size / 2, centerY + size / 2);
          if (this.maze.tiles[y]?.[x - 1] !== 0) pathBorders.lineBetween(centerX - size / 2, centerY - size / 2, centerX - size / 2, centerY + size / 2);
          if (this.maze.tiles[y]?.[x + 1] !== 0) pathBorders.lineBetween(centerX + size / 2, centerY - size / 2, centerX + size / 2, centerY + size / 2);
          if (isGateRoom && (x + y) % 2 === 0) {
            gardenDetails.fillStyle(ROUTE_COLORS.garden, 0.42);
            gardenDetails.fillCircle(centerX, centerY, 3.5);
          }
          continue;
        }

        const isPathNeighbor = [[1, 0], [-1, 0], [0, 1], [0, -1]]
          .some(([dx, dy]) => this.maze.tiles[y + dy]?.[x + dx] === 0);
        if (!isPathNeighbor) continue;
        this.add.rectangle(
          (x + 0.5) * size,
          (y + 0.5) * size,
          size,
          size,
          ROUTE_COLORS.hedge,
        ).setDepth(3);
        gardenDetails.fillStyle(ROUTE_COLORS.hedgeLight, 0.24);
        gardenDetails.fillCircle((x + 0.32) * size, (y + 0.35) * size, 7);
        gardenDetails.fillCircle((x + 0.65) * size, (y + 0.62) * size, 6);
        const physicsWall = this.add.rectangle(
          (x + 0.5) * size,
          (y + 0.5) * size,
          size - 4,
          size - 4,
          ROUTE_COLORS.hedge,
          0.001,
        ).setDepth(2);
        this.walls.add(physicsWall);
      }
    }

    const mosaic = this.add.graphics().setDepth(1.5);
    mosaic.lineStyle(2, ROUTE_COLORS.pathShade, 0.36);
    for (let y = 1; y < this.maze.height - 1; y += 3) {
      for (let x = 1; x < this.maze.width - 1; x += 4) {
        if (this.maze.tiles[y]?.[x] === 0) mosaic.strokeCircle((x + 0.5) * size, (y + 0.5) * size, 3);
      }
    }
  }

  private createPortals() {
    this.maze.portals.forEach((portal, index) => {
      const point = this.worldPoint(portal.cell);
      const color = index % 2 === 0 ? ROUTE_COLORS.portalA : ROUTE_COLORS.portalB;
      const halo = this.add.circle(point.x, point.y, ROUTE_GAME.PORTAL_RADIUS + 10, color, 0.17).setDepth(6);
      const body = this.add.circle(point.x, point.y, ROUTE_GAME.PORTAL_RADIUS, color, 0.95)
        .setStrokeStyle(4, 0xfff0b5, 0.96).setDepth(7);
      const rune = this.add.text(point.x, point.y - 2, '↔', {
        fontFamily: 'sans-serif', fontSize: '28px', color: '#fff8d0', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(8);
      this.tweens.add({ targets: [body, halo, rune], scale: { from: 0.92, to: 1.08 }, alpha: { from: 0.65, to: 1 }, duration: 850 + index * 80, yoyo: true, repeat: -1 });
    });
  }

  private createGates() {
    this.maze.gates.forEach((gate, gateIndex) => {
      const choice = this.activeLevel.choices[gate.choiceIndex];
      const point = this.worldPoint(gate.cell);
      const colors = [0xe76f51, 0x2a9d8f, 0xf4a261, 0x6a8caf, 0x9b5de5, 0x70a95d];
      const roomWidth = (gate.room.right - gate.room.left + 1) * ROUTE_GAME.TILE_SIZE;
      const roomHeight = (gate.room.bottom - gate.room.top + 1) * ROUTE_GAME.TILE_SIZE;
      // Kapı, odanın üzerinde duran ayrı bir rozet değil; cevap avlusunun
      // kendisidir. Görsel ve metin gerçek oda merkezine hizalanır.
      const portal = this.add.rectangle(point.x, point.y, roomWidth - 10, roomHeight - 10, colors[gateIndex], 0.48)
        .setStrokeStyle(4, colors[gateIndex], 1)
        .setDepth(8);
      const panelWidth = roomWidth - 30;
      const panelHeight = roomHeight - 30;
      const panel = this.add.rectangle(point.x, point.y, panelWidth, panelHeight, 0x102f31, 0.92)
        .setStrokeStyle(3, colors[gateIndex], 1).setDepth(9);
      const textureKey = choice.mediaUrl ? this.mediaTextureKeys.get(choice.mediaUrl) : undefined;
      let textCenterX = point.x;
      let textWidth = panelWidth - 22;
      if (textureKey && this.textures.exists(textureKey)) {
        const image = this.add.image(point.x - (choice.label ? panelWidth * 0.23 : 0), point.y, textureKey).setDepth(10);
        const maxImageWidth = choice.label ? panelWidth * 0.43 : panelWidth - 24;
        const maxImageHeight = choice.label ? panelHeight - 22 : panelHeight - 20;
        const scale = Math.min(maxImageWidth / Math.max(1, image.width), maxImageHeight / Math.max(1, image.height));
        image.setScale(scale);
        if (choice.label) {
          textCenterX = point.x + panelWidth * 0.24;
          textWidth = panelWidth * 0.43;
        }
      }
      if (choice.label) {
        const label = this.add.text(textCenterX, point.y, choice.label, {
          fontFamily: 'Inter, sans-serif', fontSize: '20px', color: '#fff9df', fontStyle: 'bold',
          align: 'center', wordWrap: { width: textWidth, useAdvancedWrap: true },
        }).setOrigin(0.5).setDepth(10).setMaxLines(4);
        this.fitText(label, textWidth, panelHeight - 16, 20, 11);
      }
      this.tweens.add({ targets: [portal, panel], alpha: { from: 0.78, to: 1 }, duration: 950 + gateIndex * 90, yoyo: true, repeat: -1 });
    });
  }

  /**
   * İlkokul öğrencileri alanın içine girmeyi seçim sanabilir: alan içinde dolaşırken merkezde içe doğru daralan
   * yarısaydam bir hedef belirir. Seçim yalnız merkez hücrede yapılır; işaret yalnız görseldir.
   */
  private createGateHint() {
    const size = ROUTE_GAME.TILE_SIZE;
    const glow = this.add.circle(0, 0, size * 0.62, 0xffffff, 0.28);
    const ring = this.add.circle(0, 0, size * 0.7).setStrokeStyle(5, 0xfff3b4, 0.85);
    const dot = this.add.circle(0, 0, size * 0.18, 0xfff3b4, 0.8);
    const arrows = [0, 90, 180, 270].map((angle) => {
      const radians = PhaserMath.DegToRad(angle);
      const distance = size * 1.25;
      // Üçgenin ucu merkeze bakar.
      return this.add.triangle(Math.cos(radians) * distance, Math.sin(radians) * distance, 0, -11, 0, 11, -16, 0, 0xfff3b4, 0.85)
        .setRotation(radians);
    });
    this.gateHint = this.add.container(0, 0, [glow, ring, dot, ...arrows]).setDepth(12).setVisible(false).setAlpha(0.8);
    this.tweens.add({ targets: ring, scale: { from: 1.6, to: 0.85 }, alpha: { from: 0.2, to: 0.95 }, duration: 900, repeat: -1 });
    this.tweens.add({ targets: arrows, scale: { from: 1, to: 0.8 }, duration: 450, yoyo: true, repeat: -1 });
    arrows.forEach((arrow) => {
      this.tweens.add({ targets: arrow, x: arrow.x * 0.72, y: arrow.y * 0.72, duration: 900, repeat: -1 });
    });
  }

  private updateGateHint() {
    const gate = this.state.phase === 'playing' && !this.resolving ? gateRoomAround(this.maze, this.playerCell) : undefined;
    if (!gate) {
      if (this.gateHintIndex !== null) { this.gateHint?.setVisible(false); this.gateHintIndex = null; }
      return;
    }
    if (this.gateHintIndex === gate.choiceIndex) return;
    const center = this.worldPoint(gate.cell);
    this.gateHint?.setPosition(center.x, center.y).setVisible(true);
    this.gateHintIndex = gate.choiceIndex;
  }

  private createPlayer() {
    const spawn = this.worldPoint(this.maze.playerSpawn);
    this.playerSpawnWorld.copy(spawn);
    this.playerGlow = this.add.circle(spawn.x, spawn.y, 33, ROUTE_COLORS.playerOuter, 0.22).setDepth(18);
    this.player = this.add.circle(spawn.x, spawn.y, ROUTE_GAME.PLAYER_RADIUS, ROUTE_COLORS.playerOuter, 1)
      .setStrokeStyle(4, 0xffe08a).setAlpha(0.001).setDepth(20);
    this.physics.add.existing(this.player);
    const body = this.player.body as Physics.Arcade.Body;
    body.setCircle(ROUTE_GAME.PLAYER_RADIUS).setCollideWorldBounds(true);
    body.setDrag(0, 0);
    // Rota hareketi Arcade'in duvar itmesinden bağımsız olarak hücre
    // merkezleri arasında yürür. Böylece iki sistem aynı anda konumu
    // düzeltmeye çalışıp titreşim yaratmaz.
    body.setEnable(false);
    this.playerEyes = [
      this.add.circle(spawn.x - 7, spawn.y - 3, 3.5, 0x2d2320).setAlpha(0).setDepth(22),
      this.add.circle(spawn.x + 7, spawn.y - 3, 3.5, 0x2d2320).setAlpha(0).setDepth(22),
    ];
    this.playerFlameTip = this.add.triangle(spawn.x, spawn.y - 31, -10, 12, 0, -13, 10, 12, ROUTE_COLORS.playerInner).setAlpha(0).setDepth(19);
    this.playerSprite = this.add.image(spawn.x, spawn.y - 5, AlevliRotaScene.KIVIL_TEXTURE)
      .setDisplaySize(46, 76).setDepth(23);
    this.tweens.add({ targets: this.playerFlameTip, scaleY: { from: 0.85, to: 1.2 }, angle: { from: -4, to: 4 }, duration: 280, yoyo: true, repeat: -1 });
  }

  private createEnemies() {
    const spawns = this.maze.enemySpawns.slice(0, this.enemyProfile.count);
    spawns.forEach((spawn, index) => {
      const point = this.worldPoint(spawn);
      const drop = this.add.circle(point.x, point.y, 29, ROUTE_COLORS.waterOuter, 0.18).setDepth(17);
      const bodyObject = this.add.circle(point.x, point.y, ROUTE_GAME.ENEMY_RADIUS, ROUTE_COLORS.waterOuter, 1)
        .setStrokeStyle(4, ROUTE_COLORS.waterInner).setAlpha(0.001).setDepth(19);
      this.physics.add.existing(bodyObject);
      const body = bodyObject.body as Physics.Arcade.Body;
      body.setCircle(ROUTE_GAME.ENEMY_RADIUS).setCollideWorldBounds(true);
      body.setEnable(false);
      const eyes: [GameObjects.Arc, GameObjects.Arc] = [
        this.add.circle(point.x - 6, point.y - 3, 3, 0x102a32).setAlpha(0).setDepth(21),
        this.add.circle(point.x + 6, point.y - 3, 3, 0x102a32).setAlpha(0).setDepth(21),
      ];
      const sprite = this.add.image(point.x, point.y - 4, AlevliRotaScene.CISE_TEXTURE)
        .setDisplaySize(50, 64).setDepth(22);
      const actor: EnemyActor = {
        body: bodyObject,
        sprite,
        drop,
        eyes,
        spawn,
        nextRepathAt: index * 70,
        direction: index % 2 === 0 ? 'left' : 'right',
        portalCooldownUntil: 0,
        cell: { ...spawn },
      };
      this.enemies.push(actor);
    });
  }

  private createControls() {
    this.cursors = this.input.keyboard?.createCursorKeys();
    if (this.input.keyboard) {
      this.wasd = this.input.keyboard.addKeys({
        up: 'W',
        down: 'S',
        left: 'A',
        right: 'D',
      }) as Record<'up' | 'down' | 'left' | 'right', Input.Keyboard.Key>;
      this.input.keyboard.once('keydown', () => this.audio.unlock());
    }
    this.input.once('pointerdown', () => this.audio.unlock());
    const touchCapable = navigator.maxTouchPoints > 0 || window.matchMedia('(pointer: coarse)').matches;
    if (!touchCapable) return;
    const base = this.add.circle(ROUTE_GAME.JOYSTICK_X, ROUTE_GAME.JOYSTICK_Y, ROUTE_GAME.JOYSTICK_RADIUS, 0x092d31, 0.42)
      .setStrokeStyle(4, 0xffe191, 0.7).setScrollFactor(0).setDepth(200)
      .setInteractive(
        new Geom.Circle(ROUTE_GAME.JOYSTICK_RADIUS, ROUTE_GAME.JOYSTICK_RADIUS, ROUTE_GAME.JOYSTICK_RADIUS),
        Geom.Circle.Contains,
      );
    const knob = this.add.circle(ROUTE_GAME.JOYSTICK_X, ROUTE_GAME.JOYSTICK_Y, ROUTE_GAME.JOYSTICK_KNOB_RADIUS, 0xffc857, 0.78)
      .setStrokeStyle(3, 0xffffff, 0.65).setScrollFactor(0).setDepth(201);
    this.joystick = { pointerId: null, vector: new PhaserMath.Vector2(), base, knob };
    base.on('pointerdown', (pointer: Input.Pointer) => {
      if (!this.joystick) return;
      this.joystick.pointerId = pointer.id;
      this.audio.unlock();
      this.updateJoystick(pointer);
      EventBus.emit(SPECTACLE_ACTION_EVENT, { template: 'fiery-route', action: 'joystick' });
    });
    this.input.on('pointermove', this.handlePointerMove, this);
    this.input.on('pointerup', this.handlePointerUp, this);
    this.input.on('pointerupoutside', this.handlePointerUp, this);
  }

  private handlePointerMove(pointer: Input.Pointer) {
    if (this.joystick?.pointerId === pointer.id) this.updateJoystick(pointer);
  }

  private handlePointerUp(pointer: Input.Pointer) {
    if (!this.joystick || this.joystick.pointerId !== pointer.id) return;
    this.joystick.pointerId = null;
    this.joystick.vector.set(0, 0);
    this.positionJoystickOnScreen();
  }

  private updateJoystick(pointer: Input.Pointer) {
    if (!this.joystick) return;
    const dx = pointer.x - ROUTE_GAME.JOYSTICK_X;
    const dy = pointer.y - ROUTE_GAME.JOYSTICK_Y;
    const distance = Math.hypot(dx, dy);
    const scale = distance > ROUTE_GAME.JOYSTICK_RADIUS ? ROUTE_GAME.JOYSTICK_RADIUS / distance : 1;
    const x = dx * scale;
    const y = dy * scale;
    this.joystick.vector.set(x / ROUTE_GAME.JOYSTICK_RADIUS, y / ROUTE_GAME.JOYSTICK_RADIUS);
    const direction = this.directionFromVector(this.joystick.vector.x, this.joystick.vector.y);
    if (direction) this.requestDirection(direction);
    this.positionJoystickOnScreen(x, y);
  }

  private cleanupInput() {
    this.input.off('pointermove', this.handlePointerMove, this);
    this.input.off('pointerup', this.handlePointerUp, this);
    this.input.off('pointerupoutside', this.handlePointerUp, this);
  }

  private configureCamera() {
    const camera = this.cameras.main;
    const overviewZoom = Math.min(
      this.scale.width / this.worldWidth,
      this.scale.height / this.worldHeight,
    );
    // 16:9 rota dünyası normal görünümde tam oturur. Tam ekranda araç çubuğu
    // yüzünden alan daha geniş kalırsa, oyun alanı boşluk bırakmak yerine
    // haritayı yatayda doldurur; küçük dikey kırpma kabul edilir.
    const fillZoom = Math.max(
      this.scale.width / this.worldWidth,
      this.scale.height / this.worldHeight,
    );
    camera.stopFollow();
    camera.setZoom(Math.max(overviewZoom, fillZoom * 0.98));
    camera.centerOn(this.worldWidth / 2, this.worldHeight / 2);
    const banner = this.add.text(this.worldWidth / 2, this.worldHeight / 2, `SEVİYE ${this.activeLevel.id}\nROTAYI İNCELE`, {
      fontFamily: 'Inter, sans-serif', fontSize: '44px', color: '#fff3b4', fontStyle: 'bold',
      align: 'center', backgroundColor: '#102f31e8', padding: { x: 26, y: 18 },
    }).setOrigin(0.5).setDepth(250);
    const reminder = this.add.text(this.worldWidth / 2, this.worldHeight / 2 + banner.height / 2 + 34, 'Cevabı seçmek için renkli alanın TAM ORTASINA gir', {
      fontFamily: 'Inter, sans-serif', fontSize: '26px', color: '#ffffff', fontStyle: 'bold',
      align: 'center', backgroundColor: '#102f31d9', padding: { x: 18, y: 10 },
    }).setOrigin(0.5).setDepth(250);
    this.overviewEndsAt = this.time.now + ROUTE_GAME.OVERVIEW_DURATION_MS;
    this.time.delayedCall(ROUTE_GAME.OVERVIEW_DURATION_MS, () => {
      banner.destroy();
      reminder.destroy();
      camera.pan(this.player.x, this.player.y, ROUTE_GAME.CAMERA_TWEEN_MS, 'Sine.easeInOut');
      camera.zoomTo(ROUTE_GAME.GAMEPLAY_ZOOM, ROUTE_GAME.CAMERA_TWEEN_MS, 'Sine.easeInOut');
      this.time.delayedCall(ROUTE_GAME.CAMERA_TWEEN_MS, () => {
        camera.startFollow(this.player, true, 0.12, 0.12);
        this.state.phase = 'playing';
        this.levelStartedAt = this.time.now;
        this.audio.play('start');
        this.refreshHud();
      });
    });
  }

  private simulate(delta: number, time: number) {
    if (this.state.phase !== 'playing' || this.resolving || time < this.overviewEndsAt) {
      this.updateGateHint();
      this.syncActorVisuals();
      return;
    }
    this.captureRequestedDirection();
    this.updatePlayerMovement(delta);
    this.updateEnemies(time, delta);
    this.checkRouteInteractions(time);
    this.updateGateHint();
    const invulnerable = time < this.invulnerableUntil;
    // Fizik gövdesi görünmez kalır; yalnızca karakter çizimi dokunulmazlıkta yanıp söner.
    this.player.setAlpha(0.001);
    this.playerSprite.setAlpha(invulnerable && Math.floor(time / 100) % 2 === 0 ? 0.35 : 1);
    this.syncActorVisuals();
  }

  private captureRequestedDirection() {
    if (this.cursors?.left.isDown || this.wasd?.left.isDown) this.requestDirection('left');
    else if (this.cursors?.right.isDown || this.wasd?.right.isDown) this.requestDirection('right');
    else if (this.cursors?.up.isDown || this.wasd?.up.isDown) this.requestDirection('up');
    else if (this.cursors?.down.isDown || this.wasd?.down.isDown) this.requestDirection('down');
  }

  private requestDirection(direction: RouteDirection) {
    if (this.requestedDirection !== direction) this.audio.play('turn');
    this.requestedDirection = direction;
  }

  private directionFromVector(x: number, y: number): RouteDirection | null {
    if (Math.abs(x) < 0.24 && Math.abs(y) < 0.24) return null;
    if (Math.abs(x) >= Math.abs(y)) return x < 0 ? 'left' : 'right';
    return y < 0 ? 'up' : 'down';
  }

  /** Pac-Man tarzı: her varlık tek seferde yalnız bir koridor hücresi gider. */
  private updatePlayerMovement(delta: number) {
    if (!this.playerNextCell) {
      this.playerDirection = nextContinuousDirection(
        this.maze,
        this.playerCell,
        this.playerDirection,
        this.requestedDirection,
      );
      if (!this.playerDirection) return;
      this.playerNextCell = this.neighbourForDirection(this.playerCell, this.playerDirection);
    }
    if (!this.playerNextCell) return;
    if (this.moveActorToCell(this.player, this.playerNextCell, ROUTE_GAME.PLAYER_SPEED, delta)) {
      this.playerCell = this.playerNextCell;
      this.playerNextCell = undefined;
    }
  }

  private updateEnemies(time: number, delta: number) {
    this.enemies.forEach((enemy) => {
      if (!enemy.nextCell) {
        if (time >= enemy.nextRepathAt || !enemy.targetCell) {
          enemy.targetCell = this.nextPathCell(enemy.cell, this.playerCell);
          enemy.nextRepathAt = time + this.enemyProfile.repathMs;
        }
        enemy.direction = this.directionBetween(enemy.cell, enemy.targetCell) ?? enemy.direction;
        enemy.nextCell = enemy.direction ? this.neighbourForDirection(enemy.cell, enemy.direction) : undefined;
      }
      if (!enemy.nextCell) return;
      if (this.moveActorToCell(enemy.body, enemy.nextCell, this.enemyProfile.speed, delta)) {
        enemy.cell = enemy.nextCell;
        enemy.nextCell = undefined;
        enemy.targetCell = undefined;
      }
    });
  }

  private neighbourForDirection(cell: MazeCell, direction: RouteDirection): MazeCell | undefined {
    const vector = ROUTE_DIRECTION_VECTORS[direction];
    const next = { x: cell.x + vector.x, y: cell.y + vector.y };
    return this.maze.tiles[next.y]?.[next.x] === 0 ? next : undefined;
  }

  private moveActorToCell(actor: GameObjects.Arc, targetCell: MazeCell, speed: number, delta: number): boolean {
    const target = this.worldPoint(targetCell);
    const dx = target.x - actor.x;
    const dy = target.y - actor.y;
    const distance = Math.hypot(dx, dy);
    const step = speed * (delta / 1000);
    if (distance <= step || distance < 0.01) {
      actor.setPosition(target.x, target.y);
      return true;
    }
    actor.setPosition(actor.x + (dx / distance) * step, actor.y + (dy / distance) * step);
    return false;
  }

  private checkRouteInteractions(time: number) {
    if (!this.playerNextCell) {
      const portalExit = this.portalExitForCell(this.playerCell);
      if (portalExit && time >= this.portalCooldownUntil) this.teleportPlayer(portalExit, time);
      const gate = this.maze.gates.find((candidate) => candidate.cell.x === this.playerCell.x && candidate.cell.y === this.playerCell.y);
      if (gate) this.resolveGateChoice(this.activeLevel.choices[gate.choiceIndex]);
    }

    this.enemies.forEach((enemy) => {
      if (!enemy.nextCell) {
        const portalExit = this.portalExitForCell(enemy.cell);
        if (portalExit && time >= enemy.portalCooldownUntil) this.teleportEnemy(enemy, portalExit, time);
      }
    });

    if (time < this.invulnerableUntil) return;
    const caught = this.enemies.some((enemy) => PhaserMath.Distance.Between(this.player.x, this.player.y, enemy.body.x, enemy.body.y)
      <= ROUTE_GAME.PLAYER_RADIUS + ROUTE_GAME.ENEMY_RADIUS);
    if (caught) this.onCaught();
  }

  private directionBetween(from: MazeCell, to?: MazeCell): RouteDirection | null {
    if (!to) return null;
    if (to.x < from.x) return 'left';
    if (to.x > from.x) return 'right';
    if (to.y < from.y) return 'up';
    if (to.y > from.y) return 'down';
    return null;
  }

  private nextPathCell(start: MazeCell, goal: MazeCell): MazeCell {
    const startKey = `${start.x},${start.y}`;
    const queue = [start];
    const parents = new Map<string, string | null>([[startKey, null]]);
    let reached = startKey;
    for (let index = 0; index < queue.length; index += 1) {
      const current = queue[index];
      const currentKey = `${current.x},${current.y}`;
      if (current.x === goal.x && current.y === goal.y) { reached = currentKey; break; }
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
        const next = { x: current.x + dx, y: current.y + dy };
        const nextKey = `${next.x},${next.y}`;
        if (this.maze.tiles[next.y]?.[next.x] !== 0 || parents.has(nextKey)) continue;
        parents.set(nextKey, currentKey);
        queue.push(next);
      }
      const portalExit = this.portalExitForCell(current);
      if (portalExit) {
        const portalKey = `${portalExit.x},${portalExit.y}`;
        if (!parents.has(portalKey)) {
          parents.set(portalKey, currentKey);
          queue.push(portalExit);
        }
      }
    }
    if (!parents.has(reached)) return start;
    let cursor = reached;
    let parent = parents.get(cursor);
    while (parent && parent !== startKey) {
      cursor = parent;
      parent = parents.get(cursor);
    }
    const [x, y] = cursor.split(',').map(Number);
    return { x, y };
  }

  private cellFromWorld(x: number, y: number): MazeCell {
    const raw = { x: PhaserMath.Clamp(Math.floor(x / ROUTE_GAME.TILE_SIZE), 0, this.maze.width - 1), y: PhaserMath.Clamp(Math.floor(y / ROUTE_GAME.TILE_SIZE), 0, this.maze.height - 1) };
    if (this.maze.tiles[raw.y]?.[raw.x] === 0) return raw;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      if (this.maze.tiles[raw.y + dy]?.[raw.x + dx] === 0) return { x: raw.x + dx, y: raw.y + dy };
    }
    return this.maze.playerSpawn;
  }

  private portalExitForCell(cell: MazeCell): MazeCell | undefined {
    const entrance = this.maze.portals.find((portal) => portal.cell.x === cell.x && portal.cell.y === cell.y);
    return entrance ? this.maze.portals.find((portal) => portal.id === entrance.pairId)?.cell : undefined;
  }

  private syncActorVisuals() {
    if (!this.player) return;
    this.playerGlow.setPosition(this.player.x, this.player.y);
    this.playerEyes[0].setPosition(this.player.x - 7, this.player.y - 3);
    this.playerEyes[1].setPosition(this.player.x + 7, this.player.y - 3);
    this.playerFlameTip.setPosition(this.player.x, this.player.y - 28);
    this.playerSprite.setPosition(this.player.x, this.player.y - 5);
    this.enemies.forEach((enemy) => {
      enemy.sprite.setPosition(enemy.body.x, enemy.body.y - 4);
      enemy.drop.setPosition(enemy.body.x, enemy.body.y);
      enemy.eyes[0].setPosition(enemy.body.x - 6, enemy.body.y - 3);
      enemy.eyes[1].setPosition(enemy.body.x + 6, enemy.body.y - 3);
    });
    this.positionJoystickOnScreen(
      (this.joystick?.vector.x ?? 0) * ROUTE_GAME.JOYSTICK_RADIUS,
      (this.joystick?.vector.y ?? 0) * ROUTE_GAME.JOYSTICK_RADIUS,
    );
  }

  private positionJoystickOnScreen(offsetX = 0, offsetY = 0) {
    if (!this.joystick) return;
    const inverseZoom = 1 / Math.max(0.01, this.cameras.main.zoom);
    this.joystick.base
      .setPosition(ROUTE_GAME.JOYSTICK_X * inverseZoom, ROUTE_GAME.JOYSTICK_Y * inverseZoom)
      .setScale(inverseZoom);
    this.joystick.knob
      .setPosition((ROUTE_GAME.JOYSTICK_X + offsetX) * inverseZoom, (ROUTE_GAME.JOYSTICK_Y + offsetY) * inverseZoom)
      .setScale(inverseZoom);
  }

  private onCaught() {
    if (this.resolving || this.state.phase !== 'playing' || this.time.now < this.invulnerableUntil) return;
    this.state.caught += 1;
    this.audio.play('caught');
    this.loseLife('caught');
  }

  private resolveGateChoice(choice: FieryRouteChoice | undefined) {
    if (this.resolving || this.state.phase !== 'playing') return;
    if (!choice) return;
    if (choice.correct) this.completeLevel();
    else {
      const choiceIndex = this.activeLevel.choices.indexOf(choice);
      if (choiceIndex >= 0 && !this.wrongChoiceIndices.includes(choiceIndex)) this.wrongChoiceIndices.push(choiceIndex);
      if (this.mode === 'guided') { this.revealCorrectGate(choiceIndex); return; }
      this.state.wrongGates += 1;
      this.state.score = scoreAfterWrongGate(this.state.score);
      this.state.streak = 0;
      this.audio.play('wrong');
      this.loseLife('wrong-gate');
    }
  }

  private teleportPlayer(exit: MazeCell, time: number) {
    this.portalCooldownUntil = time + ROUTE_GAME.PORTAL_COOLDOWN_MS;
    this.playerCell = { ...exit };
    this.playerNextCell = undefined;
    const point = this.worldPoint(exit);
    this.player.setPosition(point.x, point.y);
    this.invulnerableUntil = Math.max(this.invulnerableUntil, this.time.now + ROUTE_GAME.PORTAL_PROTECTION_MS);
    this.cameras.main.flash(180, 174, 242, 184);
    this.audio.play('portal');
    EventBus.emit(SPECTACLE_ACTION_EVENT, { template: 'fiery-route', action: 'portal' });
  }

  private teleportEnemy(enemy: EnemyActor, exit: MazeCell, time: number) {
    enemy.portalCooldownUntil = time + ROUTE_GAME.PORTAL_COOLDOWN_MS;
    enemy.cell = { ...exit };
    enemy.nextCell = undefined;
    const point = this.worldPoint(exit);
    enemy.body.setPosition(point.x, point.y);
    enemy.targetCell = undefined;
    enemy.nextRepathAt = 0;
  }

  /**
   * Normal mod: yanlış kapıda ✕ çıkar, kamera doğru kapıya yaklaşır ve doğru cevap yarısaydam, nabız gibi atan bir
   * vurguyla gösterilir; ardından sonraki soruya geçilir. Puan cezası ve can kaybı yoktur.
   */
  private revealCorrectGate(wrongChoiceIndex: number) {
    this.resolving = true;
    this.state.phase = 'transitioning';
    this.state.wrongGates += 1;
    this.state.streak = 0;
    this.state.questionResults.push(this.currentQuestionResult(false));
    this.audio.play('wrong');
    EventBus.emit(SPECTACLE_HIT_EVENT, { template: 'fiery-route', reason: 'wrong-gate' });
    this.refreshHud();
    this.cameras.main.shake(180, 0.006);
    const wrongGate = this.maze.gates.find((gate) => gate.choiceIndex === wrongChoiceIndex);
    const correctGate = this.maze.gates.find((gate) => this.activeLevel.choices[gate.choiceIndex]?.correct);
    if (wrongGate) {
      const point = this.worldPoint(wrongGate.cell);
      const cross = this.add.text(point.x, point.y, '✕', {
        fontFamily: 'Inter, sans-serif', fontSize: '110px', color: '#ff4d64', fontStyle: 'bold', stroke: '#3a0b14', strokeThickness: 10,
      }).setOrigin(0.5).setDepth(40).setScale(0.2).setAlpha(0.95);
      this.tweens.add({ targets: cross, scale: 1, duration: 260, ease: 'Back.easeOut' });
      this.revealObjects.push(cross);
    }
    if (correctGate) {
      const point = this.worldPoint(correctGate.cell);
      const width = (correctGate.room.right - correctGate.room.left + 1) * ROUTE_GAME.TILE_SIZE;
      const height = (correctGate.room.bottom - correctGate.room.top + 1) * ROUTE_GAME.TILE_SIZE;
      const glow = this.add.rectangle(point.x, point.y, width + 26, height + 26, 0x39f28c, 0.18).setStrokeStyle(8, 0x7dffb0, 0.9).setDepth(40).setAlpha(0);
      const label = this.add.text(point.x, point.y - height / 2 - 34, '✓ Doğru cevap', {
        fontFamily: 'Inter, sans-serif', fontSize: '30px', color: '#eafff2', fontStyle: 'bold', backgroundColor: '#0c4a33d9', padding: { x: 16, y: 8 },
      }).setOrigin(0.5).setDepth(41).setAlpha(0);
      // Önce ✕ görünür (≈ 0,65 sn), sonra kamera doğru kapıya yaklaşır ve vurgu belirir.
      this.tweens.add({ targets: [glow, label], alpha: 1, duration: 350, delay: 1_100 });
      this.tweens.add({ targets: glow, scaleX: 1.06, scaleY: 1.12, fillAlpha: 0.38, duration: 520, delay: 1_450, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
      this.revealObjects.push(glow, label);
      const camera = this.cameras.main;
      camera.stopFollow();
      this.time.delayedCall(650, () => {
        camera.pan(point.x, point.y, 700, 'Sine.easeInOut');
        camera.zoomTo(Math.min(ROUTE_GAME.GAMEPLAY_ZOOM * 1.15, camera.zoom * 1.15), 700, 'Sine.easeInOut');
      });
    }
    this.time.delayedCall(FIERY_ROUTE_ANSWER_REVEAL_MS, () => this.advanceAfterQuestion());
  }

  /** Sonraki seviyeye geçer; son soruysa sonuç ekranını açar. */
  private advanceAfterQuestion() {
    if (this.state.levelIndex === this.levels.length - 1) {
      this.state.phase = 'won';
      this.showResultScreen(true);
      return;
    }
    this.time.delayedCall(this.mode === 'guided' ? 0 : 700, () => {
      this.state.levelIndex += 1;
      this.state.phase = 'overview';
      this.scene.restart({ state: this.state });
    });
  }

  private loseLife(reason: 'caught' | 'wrong-gate') {
    this.resolving = true;
    // Normal modda can yoktur: yakalanan Kıvıl başlangıca döner, oyun bitmez.
    if (this.mode === 'challenge') this.state.lives = Math.max(0, this.state.lives - 1);
    EventBus.emit(SPECTACLE_HIT_EVENT, { template: 'fiery-route', reason });
    this.refreshHud();
    this.cameras.main.shake(220, 0.008);
    if (this.state.lives === 0) {
      this.state.questionResults.push(this.currentQuestionResult(false));
      this.state.phase = 'lost';
      this.showResultScreen(false);
      return;
    }
    this.time.delayedCall(ROUTE_GAME.RESPAWN_PAUSE_MS, () => {
      this.resetActors();
      this.invulnerableUntil = this.time.now + FIERY_ROUTE_INVULNERABILITY_MS;
      this.resolving = false;
    });
  }

  private resetActors() {
    this.player.setPosition(this.playerSpawnWorld.x, this.playerSpawnWorld.y);
    this.playerCell = { ...this.maze.playerSpawn };
    this.playerNextCell = undefined;
    this.playerDirection = 'left';
    this.requestedDirection = 'left';
    this.enemies.forEach((enemy) => {
      const point = this.worldPoint(enemy.spawn);
      enemy.body.setPosition(point.x, point.y);
      enemy.cell = { ...enemy.spawn };
      enemy.nextCell = undefined;
      enemy.nextRepathAt = 0;
      enemy.targetCell = undefined;
      enemy.direction = null;
      enemy.portalCooldownUntil = 0;
    });
  }

  private completeLevel() {
    this.resolving = true;
    this.state.phase = 'transitioning';
    this.state.questionResults.push(this.currentQuestionResult(true));
    this.state.correct += 1;
    const scored = scoreAfterCorrect(this.state.score, this.state.streak);
    this.state.score = scored.score;
    this.state.streak = scored.streak;
    this.audio.play('correct');
    EventBus.emit(SPECTACLE_HIT_EVENT, { template: 'fiery-route', reason: 'correct-gate' });
    this.refreshHud();
    if (this.state.levelIndex < this.levels.length - 1) this.cameras.main.flash(450, 255, 218, 105);
    this.advanceAfterQuestion();
  }

  private currentQuestionResult(completed: boolean): FieryRouteQuestionResult {
    const previous = this.state.questionResults;
    const previousWrong = previous.reduce((total, row) => total + row.wrongGateCount, 0);
    const previousCaught = previous.reduce((total, row) => total + row.caughtCount, 0);
    return {
      level: this.activeLevel.id,
      durationMs: Math.max(0, Math.round(this.time.now - this.levelStartedAt)),
      wrongGateCount: this.state.wrongGates - previousWrong,
      caughtCount: this.state.caught - previousCaught,
      completed,
      correctChoiceIndex: this.activeLevel.choices.findIndex((choice) => choice.correct),
      wrongChoiceIndices: [...this.wrongChoiceIndices],
    };
  }

  private refreshHud() {
    const update: RouteHudUpdate = {
      gameId: this.gameId,
      hud: {
        level: this.activeLevel.id,
        totalLevels: this.levels.length,
        question: this.activeLevel.prompt,
        questionMediaUrl: this.activeLevel.promptMediaUrl,
        phase: this.state.phase,
        score: this.state.score,
        lives: this.state.lives,
        mode: this.mode,
      },
    };
    EventBus.emit(ROUTE_HUD_UPDATE_EVENT, update);
  }

  private showResultScreen(won: boolean) {
    this.cameras.main.stopFollow();
    const camera = this.cameras.main;
    const inverseZoom = 1 / Math.max(0.01, camera.zoom);
    const totalDuration = this.state.questionResults.reduce((total, row) => total + row.durationMs, 0);
    const reviewQuestions = this.levels.map((level) => ({
      prompt: level.prompt,
      promptMediaUrl: level.promptMediaUrl,
      choices: level.choices.map((choice) => ({ text: choice.label, mediaUrl: choice.mediaUrl, correct: choice.correct })),
    }));
    const reviewItems = buildLearningReview(reviewQuestions, this.state.questionResults.map((result) => ({
      label: `${result.level}. soru`,
      questionIndex: result.level - 1,
      wrongChoiceIndices: result.wrongChoiceIndices,
      wrongCount: result.wrongGateCount,
      completed: result.completed,
      completionStatus: result.completed ? 'completed-after-mistake' : this.mode === 'guided' ? 'answer-shown' : 'incomplete',
    })));
    createLearningResultOverlay(this, {
      centerX: camera.midPoint.x,
      centerY: camera.midPoint.y,
      viewportWidth: camera.width,
      viewportHeight: camera.height,
      scale: inverseZoom,
      title: won ? 'ALEVLİ ROTA TAMAMLANDI!' : 'YOLCULUK SONA ERDİ',
      titleColor: won ? '#ffe69a' : '#ffb3a7',
      accentColor: won ? 0xffd166 : 0xe76f51,
      summaryLines: [
        `${this.state.score} puan · ${this.state.correct}/${this.levels.length} doğru · ${this.state.wrongGates} yanlış geçit`,
        `${this.state.caught} yakalanma · ${this.formatDuration(totalDuration)}`,
      ],
      reviewItems,
      restartEnabled: this.restartEnabled,
      restartLabel: 'Yeniden oyna',
      onRestart: () => {
        this.audio.play('restart');
        this.scene.restart({ state: createRouteGameState() });
      },
      resolveTextureKey: (mediaUrl) => this.mediaTextureKeys.get(mediaUrl),
    });
    this.audio.play(won ? 'complete' : 'wrong');
    this.emitResult(won);
  }

  private emitResult(completed: boolean) {
    if (this.resultEmitted) return;
    this.resultEmitted = true;
    const result: FinishPlayAttemptInput = {
      score: this.state.score,
      lives: this.state.lives,
      correct: this.state.correct,
      wrong: this.state.wrongGates,
      attempts: this.state.correct + this.state.wrongGates + this.state.caught,
      completed,
      details: { templateKey: 'fiery-route', mode: this.mode, questions: this.state.questionResults },
    };
    const event: RouteGameResult = { gameId: this.gameId, result };
    EventBus.emit(ROUTE_GAME_FINISHED_EVENT, event);
  }

  private formatDuration(milliseconds: number): string {
    const seconds = Math.round(milliseconds / 1000);
    return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  }

  private fitText(text: GameObjects.Text, maxWidth: number, maxHeight: number, preferred: number, minimum: number) {
    for (let size = preferred; size >= minimum; size -= 1) {
      text.setFontSize(size);
      if (text.width <= maxWidth && text.height <= maxHeight) return;
    }
    text.setScale(Math.min(1, maxWidth / Math.max(1, text.width), maxHeight / Math.max(1, text.height)));
  }

  private snapshot(): FieryRouteSnapshot {
    return {
      phase: this.state.phase,
      level: this.activeLevel.id,
      totalLevels: this.levels.length,
      question: this.activeLevel.prompt,
      score: this.state.score,
      lives: this.state.lives,
      wrongGates: this.state.wrongGates,
      caught: this.state.caught,
      player: { x: Math.round(this.player.x), y: Math.round(this.player.y), invulnerable: this.time.now < this.invulnerableUntil },
      enemies: this.enemies.map((enemy) => ({ x: Math.round(enemy.body.x), y: Math.round(enemy.body.y) })),
      gates: this.maze.gates.map((gate) => {
        const choice = this.activeLevel.choices[gate.choiceIndex];
        const point = this.worldPoint(gate.cell);
        return { choice: choice.label, correct: choice.correct, x: Math.round(point.x), y: Math.round(point.y) };
      }),
      gateHint: this.gateHintIndex,
      mode: this.mode,
      camera: { zoom: Number(this.cameras.main.zoom.toFixed(3)), scrollX: Math.round(this.cameras.main.scrollX), scrollY: Math.round(this.cameras.main.scrollY) },
    };
  }

  private installDebugHooks() {
    window.render_game_to_text = () => JSON.stringify(this.snapshot());
    window.advanceTime = (milliseconds: number) => {
      let remaining = Math.max(0, milliseconds);
      let virtualTime = this.time.now;
      while (remaining > 0) {
        const step = Math.min(ROUTE_GAME.MAX_DELTA_MS, remaining);
        virtualTime += step;
        this.simulate(step, virtualTime);
        remaining -= step;
      }
    };
  }
}
